import React, {useState, memo} from 'react';
import type {CSSProperties} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Image from 'next/image';
import type {StaticImageData} from 'next/image';
import Link from 'next/link';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import MUIButton from '@/components/MUI-Components/MUI-Button';
import Eyebrow from '@/components/Common/Eyebrow';
import {colors, fontSize, fontWeight, borderRadius, shadows, spacing} from '@/styles/tokens';
import parse from 'html-react-parser';
// ─── Module-level sx constants ───────────────────────────────────────────────

const tabsRowSx = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    justifyContent: 'center',
    gap: 1.5,
    mb: 6
} as const;

const getTabSx = (isActive: boolean) =>
    ({
        px: {xs: 2, sm: 3},
        py: {xs: 1, sm: 1.25},
        borderRadius: borderRadius.full,
        border: `1px solid ${colors.primary.deep}`,
        background: isActive ? colors.primary.deep : colors.background.default,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        userSelect: 'none' as const,
        '&:hover': {
            background: isActive ? colors.primary.deep : colors.background.tag
        }
    } as const);

const getTabLabelSx = (isActive: boolean) => ({
    fontSize: {xs: fontSize[14], sm: fontSize[16]},
    fontWeight: isActive ? fontWeight.bold : fontWeight.medium,
    color: isActive ? colors.text.inverse : colors.text.primary,
    lineHeight: 1.4,
    whiteSpace: 'nowrap' as const
});

const bulletRowSx = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1.5,
    mb: 1.5
} as const;

const bulletChipsRowSx = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 1
} as const;

const bulletChipSx = {
    px: 2,
    py: 0.75,
    borderRadius: borderRadius.full,
    border: `1px solid ${colors.primary.deep}`,
    bgcolor: colors.background.tag
} as const;

const imageCardSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
} as const;

const imageStyle: CSSProperties = {width: '100%', height: 'auto', maxWidth: '500px'};

// Part of the `panelOnCard` treatment: on a white card the artwork carries more of the
// panel, so it runs wider and picks up the card's corner radius.
const imageOnCardStyle: CSSProperties = {width: '100%', height: 'auto', maxWidth: '620px', borderRadius: borderRadius.xl};

const contentPanelSx = {
    animation: 'dmFadeIn 0.3s ease-in-out'
} as const;

// Opt-in surface for a section sitting on the tinted page band, where the panel needs
// to lift off it. The padding comes with the background — a white block flush to the
// copy would read as a bleed rather than a card.
const contentPanelCardSx = {
    ...contentPanelSx,
    bgcolor: colors.background.default,
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius['2xl'],
    boxShadow: shadows.lg,
    p: {xs: spacing[20], md: spacing[32]}
} as const;
// ─────────────────────────────────────────────────────────────────────────────

export interface DecisionMakerTab {
    label: string;
    title: string;
    description: string;
    bullets: string[];
    /** Optional closing line under the bullets — context, not a promise, so no check. */
    note?: string;
    /** Omit to render the panel without a CTA. */
    ctaText?: string;
    ctaUrl?: string;
    /** A CDN path or an imported image — `next/image` takes either. */
    image: string | StaticImageData;
    imageAlt: string;
}

interface HomeDecisionMakersProps {
    eyebrow?: string;
    heading: string;
    subheading: string;
    tabs: DecisionMakerTab[];
    onStartupCta?: () => void;
    /**
     * Renders the active panel on a white card instead of straight on the section
     * background, with the artwork run wider and rounded to match. For sections placed
     * on the tinted page band.
     */
    panelOnCard?: boolean;
    /** `check` (default) renders each bullet with a check icon; `chip` renders them as pills. */
    bulletVariant?: 'check' | 'chip';
}

const HomeDecisionMakers = ({
    eyebrow,
    heading,
    subheading,
    tabs,
    onStartupCta,
    panelOnCard,
    bulletVariant = 'check'
}: HomeDecisionMakersProps) => {
    const [activeIndex, setActiveIndex] = useState(0);

    return (
        <MUIContainer>
            {eyebrow && <Eyebrow text={eyebrow} sx={{textAlign: 'center'}} />}
            <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                <Typography variant="h1" component="h2" mb={3} fontSize={{xs: 35, sm: 44, md: 48, lg: 52}} className="font-headers">
                    {heading ? parse(heading) : ''}
                </Typography>
                <Typography variant="subtitle1" component="p" color="text.primary" className="font-regular-text">
                    {subheading ? parse(subheading) : ''}
                </Typography>
            </Box>

            {/* Tabs */}
            <Box sx={tabsRowSx}>
                {tabs.map((tab, idx) => (
                    <Box key={idx} sx={getTabSx(activeIndex === idx)} onClick={() => setActiveIndex(idx)}>
                        <Typography sx={getTabLabelSx(activeIndex === idx)} className="font-headers">
                            {tab.label}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Content panels — render ALL personas so every panel's content is
                in the server HTML for crawlers (SEO #33); only the active one is
                shown (others hidden via the `hidden` attribute / display:none). */}
            {tabs.map((tabItem, tabIdx) => (
                <Box sx={panelOnCard ? contentPanelCardSx : contentPanelSx} key={tabIdx} hidden={activeIndex !== tabIdx}>
                    <Grid container spacing={4} alignItems="center">
                        {/* Left — text */}
                        <Grid item xs={12} md={6}>
                            <Box>
                                <Typography variant="h3" component="h3" fontSize={22} mb={1.2} className="font-headers">
                                    {tabItem.title}
                                </Typography>
                                <Typography component="p" mb={2} color="text.primary" className="font-regular-text">
                                    {tabItem.description}
                                </Typography>
                                <Box mb={3} sx={bulletVariant === 'chip' ? bulletChipsRowSx : undefined}>
                                    {tabItem.bullets.map((bullet, i) =>
                                        bulletVariant === 'chip' ? (
                                            <Box key={i} sx={bulletChipSx}>
                                                <Typography color="text.primary" className="font-regular-text" fontSize={14}>
                                                    {bullet}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Box key={i} sx={bulletRowSx}>
                                                <CheckIcon />
                                                <Typography color="text.primary" className="font-regular-text">
                                                    {bullet}
                                                </Typography>
                                            </Box>
                                        )
                                    )}
                                </Box>
                                {tabItem.note && (
                                    <Typography component="p" mb={3} color="text.secondary" className="font-regular-text">
                                        {tabItem.note}
                                    </Typography>
                                )}
                                {tabItem.ctaText &&
                                    (tabIdx === 0 && onStartupCta ? (
                                        <MUIButton
                                            label={tabItem.ctaText}
                                            variant="outlined"
                                            size="medium"
                                            endIcon={<ArrowForwardIcon />}
                                            disableRipple={true}
                                            className="moving-icon-button secondary-button font-headers"
                                            onClick={onStartupCta}
                                        />
                                    ) : (
                                        <Link
                                            href={tabItem.ctaUrl ?? '#'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{textDecoration: 'none', display: 'inline-block'}}
                                        >
                                            <MUIButton
                                                label={tabItem.ctaText}
                                                variant="outlined"
                                                size="medium"
                                                endIcon={<ArrowForwardIcon />}
                                                disableRipple={true}
                                                className="moving-icon-button secondary-button font-headers"
                                            />
                                        </Link>
                                    ))}
                            </Box>
                        </Grid>

                        {/* Right — image */}
                        <Grid item xs={12} md={6}>
                            <Box sx={imageCardSx}>
                                <Image
                                    src={tabItem.image}
                                    alt={tabItem.imageAlt}
                                    width={600}
                                    height={400}
                                    style={panelOnCard ? imageOnCardStyle : imageStyle}
                                    sizes="(max-width:900px) 100vw, 50vw"
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            ))}

            <style>{`
                @keyframes dmFadeIn {
                    from { opacity: 0; transform: translateY(6px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </MUIContainer>
    );
};

export default memo(HomeDecisionMakers);
